import { createSlice } from '@reduxjs/toolkit';
import { ObjectTypes } from '../../utils/constants';
import { parseJwt } from '../../utils/jwt';

const extractUserObjects = (userObjects) => {
  const userObjs = {
    assignedDivisions: [],
    assignedClients: [],
    assignedLocations: [],
    assignedSourceSystems: [],
    assignedProjects: [],
    assignedSuppliers: [],
    assignedContracts: [],
    assignedFleets: []
  };

  if (!Array.isArray(userObjects) || userObjects.length === 0) {
    return userObjs;
  }

  const getConfigData = (objectType) => {
    const configData = userObjects.find((x) => x.ObjectType === objectType);
    if (configData && configData.ObjectData) {
      return JSON.parse(configData.ObjectData);
    }

    return null;
  };

  userObjs.assignedDivisions = getConfigData(ObjectTypes.AssignedDivisions) || [];
  userObjs.assignedClients = getConfigData(ObjectTypes.AssignedClients) || [];
  userObjs.assignedLocations = getConfigData(ObjectTypes.AssignedLocations) || [];
  userObjs.assignedSourceSystems = getConfigData(ObjectTypes.AssignedSourceSystems) || [];
  userObjs.assignedProjects = getConfigData(ObjectTypes.AssignedProjects) || [];
  userObjs.assignedSuppliers = getConfigData(ObjectTypes.AssignedSuppliers) || [];
  userObjs.assignedContracts = getConfigData(ObjectTypes.AssociateContracts) || [];
  userObjs.assignedFleets = getConfigData(ObjectTypes.FleetAssociates) || [];
  return userObjs;
};

const parseRoleClaims = (roleClaims) => {
  const emptySet = {};
  try {
    if (!Array.isArray(roleClaims) || roleClaims.length === 0) {
      return emptySet;
    }

    const permissionClaim = roleClaims.find(
      (c) => c.ClaimName === 'Permission'
    );
    if (!permissionClaim) {
      return emptySet;
    }

    const permissionCodes = JSON.parse(permissionClaim.ClaimValue) || [];
    return permissionCodes.reduce((previousValue, currentValue) => {
      if (currentValue) {
        previousValue[currentValue.toLowerCase()] = true;
      }

      return previousValue;
    }, {});
  } catch (error) {
    console.log(error);
  }

  return emptySet;
};

const initialState = {
  isAuthenticated: false,
  loginUser: null,
  impersonatedUser: null,
  isValidatingToken: false
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // updateAuthData(state, action) {
    //   Object.keys(initialState).forEach((key) => {
    //     state[key] = action.payload[key];
    //   });
    // },

    updateLoginUser(state, action) {
      const {
        isAuthenticated,
        user,
        roleClaims,
        token,
        userObjects,
        dateFormat,
        dateTimeFormat,
        isClientOnHold,
      } = action.payload;
      const {
        assignedDivisions,
        assignedClients,
        assignedLocations,
        assignedSourceSystems,
        assignedProjects,
        assignedSuppliers,
        assignedContracts,
        assignedFleets
      } = extractUserObjects(userObjects);

      const { uname: username } = parseJwt(token) || {};

      state.isAuthenticated = isAuthenticated;
      const permissionSet = parseRoleClaims(roleClaims);
      state.loginUser = {
        user,
        username,
        assignedDivisions,
        assignedClients,
        assignedLocations,
        assignedSourceSystems,
        assignedProjects,
        assignedSuppliers,
        assignedContracts,
        assignedFleets,
        dateFormat,
        dateTimeFormat,
        permissionSet,
        isClientOnHold
      };
    },
    logout(state) {
      state.isAuthenticated = false;
      state.loginUser = null;
    },
    startImpersonating(state, action) {
      const {
        UserAccountID,
        UserID,
        Username,
        dateFormat,
        dateTimeFormat,
        user,
        roleClaims,
        userObjects,
      } = action.payload;

      const {
        assignedDivisions,
        assignedClients,
        assignedLocations,
        assignedSourceSystems,
        assignedProjects,
        assignedSuppliers,
        assignedContracts,
        assignedFleets
      } = extractUserObjects(userObjects);

      const permissionSet = parseRoleClaims(roleClaims);

      state.impersonatedUser = {
        UserAccountID,
        UserID,
        Username,
        dateFormat,
        dateTimeFormat,
        user,
        assignedDivisions,
        assignedClients,
        assignedContracts,
        assignedFleets,
        assignedLocations,
        assignedSourceSystems,
        assignedProjects,
        assignedSuppliers,
        permissionSet,
      };

      state.isImpersonateing = true;
    },
    stopImpersonating(state) {
      state.isImpersonateing = false;
      state.impersonatedUser = null;
    },
    updateProfileInfo(state, action) {
      const { user } = action.payload;
      state.loginUser.user = user;
      state.loginUser.dateFormat = user.DateFormat;
      state.loginUser.dateTimeFormat = user.DateTimeFormat;
    },
    updateValidatingToken(state, action) {
      state.isValidatingToken = action.payload;
    },
  },
});

export const authActions = authSlice.actions;
export default authSlice.reducer;
